import { useMemo } from "react";
import { Badge, Button, Card, Image, Typography, Flex } from "antd";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";

import {
  NextArrow,
  PrevArrow,
} from "../../Components/Common/reactSlickCustomArrows";

const { Text } = Typography;

export default function BestSellerCarousel({
  category,
  screens,
  settings,
  toSlug,
}) {
  const navigate = useNavigate();

  const products = category?.Products ?? [];

  const isMobile = screens?.xs;

  const { canSlide } = useMemo(() => {
    const desired = screens?.md ? 2 : 1;
    const total = products.length;
    const slides = Math.min(desired, total);

    return { slidesToShow: slides, canSlide: total > slides };
  }, [screens?.md, products.length]);

  if (!category || products.length === 0) return null;

  const sliderSettings = {
    variableWidth: !screens.xs,
    slidesToScroll: 1,
    infinite: canSlide,
    autoplay: canSlide,
    arrows: canSlide,
    dots: false,
    swipeToSlide: true,
    draggable: true,
    accessibility: false, // 🔥 stops aria-hidden click madness
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
  };

  return (
    <Flex
      style={{
        marginTop: 40,
        marginBottom: 40,
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
        flexDirection: isMobile ? "column" : "row",
        gap: 0,
        maxWidth: 2500,
      }}
    >
      {/* LEFT – Yellow (overlapping) */}
      <div
        style={{
          backgroundColor: "#FFDC00",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: isMobile ? "center" : "flex-start",
          padding: 24,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",

          // desktop sizing
          flex: isMobile ? "unset" : "0 0 420px",

          // ✅ remove overlap on mobile
          marginRight: isMobile ? 0 : -160,

          // ✅ mobile should not be super tall
          minHeight: isMobile ? 80 : 440,
          height: isMobile ? "auto" : "100%",

          width: isMobile ? "100%" : "auto",
          marginBottom: isMobile ? -20 : 0,
        }}
      >
        <p
          className="category-title"
          style={{
            fontSize: screens?.xs ? 20 : 30,
            margin: 0,
            textAlign: screens?.xs ? "center" : "left",
            width: 200,
          }}
        >
          {category.CategoryName}
        </p>

        <Text
          className="view-more-title"
          style={{
            fontSize: screens.xs ? 10 : 14,
          }}
          onClick={() =>
            navigate("/products", {
              state: {
                category: {
                  Id: category.Id,
                },
              },
            })
          }
        >
          View more &gt;
        </Text>
      </div>

      {/* RIGHT – Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          marginTop: isMobile ? 20 : 10,

          // ✅ on desktop, push carousel right so overlap doesn't cover it
          // paddingLeft: isMobile ? 0 : 140,
        }}
      >
        <Slider
          {...sliderSettings}
          style={
            {
              //  marginTop: 30,
            }
          }
        >
          {products.map((item) => {
            const card = (
              <Card
                styles={{
                  body: {
                    paddingTop: 10,
                    paddingBottom: 0,
                    width: "100%",
                    minWidth: 225,
                  },
                  actions: {
                    paddingLeft: 20,
                    paddingRight: 20,
                    paddingTop: 0,
                    paddingBottom: 0,
                    margin: 0,
                    borderTop: "none",
                  },
                }}
                style={{
                  cursor: "initial",
                  width: screens?.xs ? "100%" : 280,
                  marginTop: 10,
                  overflow: "hidden",
                }}
                hoverable={false}
                title={(item.SubCategoryNames ?? []).map((sub, i, arr) => (
                  <Text
                    key={`${sub}-${i}`}
                    style={{
                      fontSize: screens?.xs ? 12 : 14,
                      color: "#6B7280",
                      marginRight: 5,
                    }}
                  >
                    {sub}
                    {i < arr.length - 1 ? "," : ""}
                  </Text>
                ))}
                cover={
                  <Image
                    width={screens.xs || screens.md ? "100%" : 240}
                    height={200}
                    alt={item.ProductName}
                    style={{
                      borderRadius: 4,
                      overflow: "hidden",
                      objectFit: "contain",
                    }}
                    src={(settings?.productImageFolder ?? "") + item.CoverImage}
                  />
                }
                actions={[
                  <Button
                    key="view"
                    type="primary"
                    block
                    style={{
                      marginTop: 0,
                      backgroundColor: "#002753",
                      color: "#fff",
                    }}
                    className="view-details-btn"
                    onClick={() => {
                      navigate(
                        `/product/${toSlug(item.ProductName)}/${toSlug(
                          item.Sku,
                        )}/${item.ProductId}`,
                      );
                    }}
                  >
                    View Details
                  </Button>,
                ]}
              >
                <Typography.Paragraph
                  ellipsis={{
                    rows: 2,
                    tooltip: item.ProductName,
                  }}
                  style={{
                    width: "100%",
                    minHeight: 50.5,
                    margin: 0,
                    fontSize: 16,
                    color: "#002753",
                    fontWeight: "bold",
                  }}
                >
                  {item.ProductName}
                </Typography.Paragraph>

                <p
                  style={{
                    margin: 0,
                    marginTop: 5,
                    fontSize: 14,
                    color: "#6B7280",
                  }}
                >
                  {item.Sku}
                </p>
              </Card>
            );

            let wrapped = card;

            if (item.Promotions && item.Promotions.length > 0) {
              wrapped = (
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: -12,
                      background: "#FFDC00",
                      padding: "2px 10px",
                      transform: "rotate(-45deg)",
                      fontWeight: "bold",
                      zIndex: 1,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                      borderRadius: 4,
                      overflow: "hidden",
                      color: "black",
                    }}
                  >
                    Promo
                  </div>
                  {wrapped}
                </div>
              );
            }

            if (item.RibbonText) {
              wrapped = (
                <Badge.Ribbon text={item.RibbonText} color="red">
                  {wrapped}
                </Badge.Ribbon>
              );
            }

            return (
              <div key={item.ProductId} className="find-me">
                {wrapped}
              </div>
            );
          })}
        </Slider>
      </div>
    </Flex>
  );
}
